import {
  ReactElement,
  useState,
  useEffect,
  ChangeEvent,
  forwardRef,
  ForwardedRef,
  ComponentPropsWithoutRef
} from 'react';
import { Flexbox } from '@morressier/ts';
import { faSquareExclamation } from '@fortawesome/pro-solid-svg-icons';
import { typedBoolean } from 'utils/array';
import {
  StyledInput,
  Container,
  IconContainer,
  HelperText,
  ErrorText,
  ErrorIcon,
  Limit
} from './style';

type InputValue = string | number | undefined;

interface InputProps extends ComponentPropsWithoutRef<'input'> {
  startIcon?: ReactElement | null;
  endIcon?: ReactElement | null;
  value?: InputValue;
  limit?: number;
  error?: string;
  hasError?: boolean;
}

const Input = (props: InputProps, ref: ForwardedRef<HTMLInputElement>) => {
  const [internalValue, setInternalValue] = useState<string | number>('');
  const {
    limit,
    error,
    startIcon,
    endIcon,
    value,
    onChange,
    className,
    hasError,
    disabled,
    ...rest
  } = props;

  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    setInternalValue(e.target.value);
  };

  const valueLength = (internalValue ?? '').toString().length;

  return (
    <>
      <Container
        hasError={hasError || typedBoolean(error)}
        className={className}
        disabled={disabled}
      >
        {startIcon && (
          <IconContainer position="start">{startIcon}</IconContainer>
        )}
        <StyledInput
          ref={ref}
          value={internalValue}
          onChange={handleChange}
          disabled={disabled}
          {...rest}
        />
        {endIcon && <IconContainer position="end">{endIcon}</IconContainer>}
      </Container>
      {(limit || error) && (
        <HelperText>
          {Boolean(error) && (
            <Flexbox alignItems="center" gap="4px">
              <ErrorIcon icon={faSquareExclamation} />
              <ErrorText>{error}</ErrorText>
            </Flexbox>
          )}
          {limit && (
            <Limit isLimitExceeded={valueLength > limit}>
              {valueLength} / {limit}
            </Limit>
          )}
        </HelperText>
      )}
    </>
  );
};

export default forwardRef(Input);
